import { sortBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import {
  AUDIT_LOG,
  FEATURE_FLAG,
  SSO,
  TEMP_PARTNER_VISIBILITY,
} from '@/constants/feature_flags';
import {
  useBillingStore,
  useFeatureFlagStore,
  usePermissionsStore,
} from '@/stores';

export function useRoleResources() {
  const featureFlagStore = useFeatureFlagStore();
  const billingStore = useBillingStore();
  const permissionsStore = usePermissionsStore();

  const { isSupernodeOrEnterpriseTier, isEnterpriseTier } =
    storeToRefs(billingStore);
  const { allResources } = storeToRefs(permissionsStore);

  const ssoFeatureEnabled = computed(
    () =>
      featureFlagStore.hasFeatureFlag(SSO) || isSupernodeOrEnterpriseTier.value,
  );
  const auditLogEnabled = computed(
    () =>
      featureFlagStore.hasFeatureFlag(AUDIT_LOG) ||
      isSupernodeOrEnterpriseTier.value,
  );
  const partnerVisibilityEnabled = computed(
    () =>
      featureFlagStore.hasFeatureFlag(TEMP_PARTNER_VISIBILITY) ||
      isEnterpriseTier.value,
  );

  const visibleResources = computed(() => {
    /* members and roles are filtered out because of a pentest finding: if you
    can manage roles you can edit your own role and escalate your own
    privileges, for example into data sharing */
    return sortBy(
      allResources.value.filter((resource) => {
        if (
          resource.name === 'members' ||
          resource.name === 'roles' ||
          (resource.name === 'audit-log' && !auditLogEnabled.value) ||
          (resource.name === 'sso' && !ssoFeatureEnabled.value) ||
          resource.name === 'threads' ||
          (resource.name === 'partner-visibility' &&
            !partnerVisibilityEnabled.value) ||
          resource.name === 'partner-creation' ||
          resource.name == 'deal-navigator' ||
          (resource.name === 'agents' &&
            !featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_AGENTS)) ||
          (resource.name === 'mcp' &&
            !featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_PERMISSIONS))
        ) {
          return false;
        }

        return true;
      }),
      ['displayName'],
    );
  });

  const hasRoleEditableResource = computed(
    () =>
      featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_PERMISSIONS) &&
      visibleResources.value.some((resource) => resource.isRoleEditable),
  );

  return { visibleResources, hasRoleEditableResource };
}
